import styled from '@emotion/styled';
import { Button, useTheme } from '@mui/material';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { ReactComponent as AddIcon } from '../../../assets/icons/add-blue.svg';
import { AvatarCell } from '../../../components/Table/CellRenderers/AvatarCell';
import { TableHeaderCell } from '../../../components/Table/CellRenderers/TableHeaderCell';
import { Typography } from '../../../components/Typography/Typography';
import { Switch } from '../../../components/SwitchButton/Switch';
import { LoadingId, SelectItem, User } from '../../../types';
import { AssignProxyModal } from '../components/AssignProxyModal/AssignProxyModal';
import { UnassignProxy } from '../components/UnassignProxy';
import { useAtomValue } from 'jotai';
import { isSmallScreenState } from '../../../state/UIState';
import { useAssignProxyToOperatingPartner } from '../../../queries/hooks/useAssignProxyToOperatingPartner';
import { useLoadingBar } from '../../../hooks/useLoadingBar';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { extractErrorMessage } from '../../../utils/errorNotifier';

const CellWrapper = styled('div')`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const columnHelper = createColumnHelper<User>();

/**
 * SVP-820: per-submitter switch deciding whether their proxy may SUBMIT their weekly timesheets or only
 * fill them. Disabled without a proxy, because the permission is granted to the person in that slot and
 * the API rejects setting it on an unassigned submitter.
 */
const CanSubmitCell = ({ user }: { user: User }) => {
  const { mutate: onAssignProxyUser, isLoading: updateInProgress } =
    useAssignProxyToOperatingPartner();
  const { pushSuccessToast, pushErrorToast } = useToastMessage();
  const { startLoading, stopLoading } = useLoadingBar();

  const canSubmit = !!user?.proxyCanSubmit;
  const hasProxy = !!user?.proxy;

  const handleToggle = useCallback(() => {
    startLoading(LoadingId.assignProxyToUser);
    onAssignProxyUser(
      { id: user.id, proxyCanSubmit: !canSubmit },
      {
        onSuccess: () => {
          pushSuccessToast({
            title: canSubmit ? 'Submitting disabled' : 'Submitting enabled',
            message: `${user.proxy?.name} can ${
              canSubmit ? 'no longer' : 'now'
            } submit timesheets for ${user.name}`,
          });
        },
        onError: (error: unknown) => {
          pushErrorToast({ message: extractErrorMessage(error) });
        },
        onSettled: () => {
          stopLoading(LoadingId.assignProxyToUser);
        },
      }
    );
  }, [
    canSubmit,
    onAssignProxyUser,
    pushErrorToast,
    pushSuccessToast,
    startLoading,
    stopLoading,
    user.id,
    user.name,
    user.proxy?.name,
  ]);

  // Nothing at all without a proxy: there is no one to grant the permission to, and a placeholder glyph
  // just adds noise down a column that is mostly empty.
  if (!hasProxy) {
    return null;
  }

  return <Switch checked={canSubmit} disabled={updateInProgress} onChange={handleToggle} />;
};

export const ActionCell = (props: CellContext<User, unknown>) => {
  const { colors } = useTheme();
  const isSmallScreen = useAtomValue(isSmallScreenState);
  const user = props.row.original;

  const hasProxy = !!user?.proxy;

  const [assignProxyModalOpen, setAssignProxyModalOpen] = useState(false);

  const handleAssignProxyModalOpen = useCallback(() => {
    setAssignProxyModalOpen(true);
  }, []);

  const handleAssignProxyModalClose = useCallback(() => {
    setAssignProxyModalOpen(false);
  }, []);

  return (
    <CellWrapper style={{ justifyContent: 'end', paddingRight: '12px', gap: '8px' }}>
      {hasProxy && <UnassignProxy user={user} />}
      <Button
        onClick={handleAssignProxyModalOpen}
        variant='text'
        style={{
          height: '28px',
          backgroundColor: colors.surfaceBackground.bg1,
          border: `1px solid ${colors.border.default}`,
        }}
        startIcon={<AddIcon />}
        sx={{
          '.MuiButton-startIcon': { marginRight: '2px' },
          '&.Mui-disabled': { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'auto' },
          svg: { g: { path: { fill: colors.icon.accent } } },
        }}
      >
        {!isSmallScreen ? (
          <Typography
            variant='h4'
            color={colors.textAccent.default}
            style={{ marginBottom: '-2px' }}
          >
            Assign Proxy
          </Typography>
        ) : null}
      </Button>
      {assignProxyModalOpen && (
        <AssignProxyModal
          assignProxyModalOpen={assignProxyModalOpen}
          handleAssignProxyModalClose={handleAssignProxyModalClose}
          user={user}
        />
      )}
    </CellWrapper>
  );
};

export const useProxyAssignmentsTableColumns = () => {
  const { colors } = useTheme();
  const isSmallScreen = useAtomValue(isSmallScreenState);

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        cell: (info) => (
          <Typography variant='body' color={colors.text.caption}>
            {info.getValue()}
          </Typography>
        ),
        header: () => <TableHeaderCell text='ID' />,
        meta: {
          width: '50px',
          minWidth: '50px',
          maxWidth: '50px',
          tdStyles: { paddingLeft: '16px', borderRight: `none` },
          thStyles: { borderRight: `none`, paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor('name', {
        cell: (info) => (
          <AvatarCell
            avatarBoxStyle={{
              background: colors.surfaceIndicator.needsReview,
            }}
            name={info.getValue()}
          />
        ),
        header: () => <TableHeaderCell text='Submitter' />,
        filterFn: (row, id, filterValue: SelectItem[]) => {
          if (!filterValue.length) return true;
          return filterValue.some((user) => user.id === row.original.id);
        },
        meta: {
          width: '30%',
          maxWidth: '30%',
          minWidth: '30%',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor('proxy', {
        cell: (info) =>
          info.getValue() ? (
            <AvatarCell
              avatarBoxStyle={{
                background: colors.surfaceIndicator.needsReview,
              }}
              name={info.getValue()?.name as string}
            />
          ) : (
            <Typography variant='h5' color={colors.text.main}>
              No Proxy
            </Typography>
          ),
        header: () => <TableHeaderCell text='Proxy' />,
        filterFn: (row, id, filterValue) => {
          const isUserNameMatched = (row.original.name ?? '')
            .toLowerCase()
            .includes(filterValue.toLowerCase());
          const isProxyMatched = (row.original?.proxy?.name ?? '')
            .toLowerCase()
            .includes(filterValue.toLowerCase());
          return isUserNameMatched || isProxyMatched;
        },
        meta: {
          width: '35%',
          maxWidth: '35%',
          minWidth: '35%',
        },
      }),
      columnHelper.display({
        id: 'proxyCanSubmit',
        cell: (info) => <CanSubmitCell user={info.row.original} />,
        header: () => <TableHeaderCell text='Can submit timesheets' />,
        meta: {
          width: '25%',
          maxWidth: '25%',
          minWidth: '25%',
          // Keep the default border-right so this column is divided from the actions column like every
          // other pair. The actions column drops its own border-left, so that stays a single line.
          tdStyles: { paddingLeft: '16px' },
          thStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => <ActionCell {...info} />,
        header: () => <TableHeaderCell text='' />,
        meta: {
          width: isSmallScreen ? '150px' : '400px',
          minWidth: isSmallScreen ? '150px' : '400px',
          maxWidth: isSmallScreen ? '150px' : '400px',
          tdStyles: { paddingLeft: '0', borderLeft: `none` },
          thStyles: { borderLeft: `none` },
        },
      }),
    ],
    [colors, isSmallScreen]
  );

  return columns;
};
